import React, { useState, useEffect } from 'react';
import { formatStepError } from './stepError';
import type { DoctorBankDetails, DoctorBankDetailsInput } from '../../../types/doctorOnboarding';

type Props = {
  doctorId: number | null;
  bankDetails: DoctorBankDetails | null;
  /** Session-only: pre-fill account number when user goes back (API returns masked only) */
  lastSavedAccountNumber?: string | null;
  onSave: (body: DoctorBankDetailsInput) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

export default function BankDetailsStep({
  doctorId,
  bankDetails,
  lastSavedAccountNumber,
  onSave,
  onNext,
  onBack,
}: Props) {
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [accountType, setAccountType] = useState<'savings' | 'current'>('savings');
  const [upiId, setUpiId] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canContinue = !!bankDetails;

  useEffect(() => {
    if (!bankDetails) return;
    setAccountHolderName(bankDetails.account_holder_name || '');
    setIfscCode(bankDetails.ifsc_code || '');
    setBankName(bankDetails.bank_name || '');
    setBranchName(bankDetails.branch_name || '');
    setAccountType((bankDetails.account_type as 'savings' | 'current') || 'savings');
    setUpiId(bankDetails.upi_id || '');
    setPanNumber(bankDetails.pan_number || '');
    setGstNumber(bankDetails.gst_number || '');
    // Pre-fill account number when user returns to step (API returns masked only; we keep full number in session)
    if (lastSavedAccountNumber != null && lastSavedAccountNumber !== '') {
      setAccountNumber(lastSavedAccountNumber);
      setConfirmAccountNumber(lastSavedAccountNumber);
    }
  }, [bankDetails, lastSavedAccountNumber]);

  const isValid =
    !!doctorId &&
    accountHolderName.trim() &&
    accountNumber.trim().length >= 9 &&
    accountNumber === confirmAccountNumber &&
    ifscCode.trim().length === 11;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !doctorId) return;
    setLoading(true);
    setError(null);
    try {
      await onSave({
        doctor_id: doctorId,
        account_holder_name: accountHolderName.trim(),
        account_number: accountNumber.trim(),
        ifsc_code: ifscCode.trim().toUpperCase(),
        bank_name: bankName.trim() || undefined,
        branch_name: branchName.trim() || undefined,
        account_type: accountType,
        upi_id: upiId.trim() || undefined,
        pan_number: panNumber.trim() || undefined,
        gst_number: gstNumber.trim() || undefined,
      });
      setAccountNumber('');
      setConfirmAccountNumber('');
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to save bank details'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Bank Details</h3>
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>Account Holder Name <span className="required">*</span></label>
            <input value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Account Number <span className="required">*</span></label>
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Confirm Account Number <span className="required">*</span></label>
              <input value={confirmAccountNumber} onChange={(e) => setConfirmAccountNumber(e.target.value)} />
            </div>
          </div>
          {accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber && (
            <span className="field-hint">Account numbers do not match</span>
          )}
          <div className="form-group">
            <label>IFSC Code <span className="required">*</span></label>
            <input value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} maxLength={11} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Bank Name</label>
              <input value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Branch Name</label>
              <input value={branchName} onChange={(e) => setBranchName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Account Type <span className="required">*</span></label>
            <select value={accountType} onChange={(e) => setAccountType(e.target.value as 'savings' | 'current')}>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>UPI ID (optional)</label>
              <input value={upiId} onChange={(e) => setUpiId(e.target.value)} />
            </div>
            <div className="form-group">
              <label>PAN Number (optional)</label>
              <input value={panNumber} onChange={(e) => setPanNumber(e.target.value)} maxLength={10} />
            </div>
          </div>
          <div className="form-group">
            <label>GST Number (optional)</label>
            <input value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} maxLength={15} />
          </div>
          {error && (
            <div className="onboarding-error" role="alert">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={!isValid || loading}>
            {loading ? 'Saving...' : 'Save Bank Details'}
          </button>
        </form>
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        {error ? (
          <span className="bank-details-step-error" role="alert">
            {error}
          </span>
        ) : !canContinue ? (
          <span className="field-hint">Save bank details to continue.</span>
        ) : null}
        <button type="button" className="btn-primary" onClick={onNext} disabled={!canContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
